import { z } from "zod"

/** Default shown by operator surfaces. Each preview still sends it explicitly. */
export const LIVE_OFFER_REBUILD_DEFAULT_FLOOR = 20

/** Defensive input ceiling. Marketplace stock is far below this in normal operation. */
export const LIVE_OFFER_REBUILD_MAX_FLOOR = 1_000_000

export const liveOfferRebuildFloorSchema = z
  .number({
    error: "stock floor is required and must be a number",
  })
  .int("stock floor must be a whole number")
  .min(0, "stock floor must be 0 or greater")
  .max(
    LIVE_OFFER_REBUILD_MAX_FLOOR,
    `stock floor must be ${LIVE_OFFER_REBUILD_MAX_FLOOR.toLocaleString("en-US")} or less`
  )

export const MIRAKL_OFFER_REBUILD_KIND = "mirakl-offer-rebuild"
export const OCTOPIA_LIVE_OFFER_REBUILD_KIND = "octopia-live-offer-rebuild"

/** Durable proof that an Octopia package crossed the irreversible Ready edge.
 * It is persisted before polling, so an error or API restart cannot make the
 * reviewed artifact look safe to submit again. */
export const liveOfferRebuildSubmissionReceiptSchema = z.object({
  reviewedJobId: z.string().min(1),
  packageId: z.string().min(1),
  submitted: z.literal(true),
})

const rebuildTotalsSchema = z.object({
  read: z.number().int().nonnegative(),
  zeroed: z.number().int().nonnegative(),
  alreadyZero: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
})

const miraklRebuildRowSchema = z.object({
  sku: z.string(),
  name: z.string().nullable(),
  quantityBefore: z.number(),
  quantityAfter: z.number(),
  changes: z.boolean(),
  price: z.string().nullable(),
  state: z.string().nullable(),
  productId: z.string().nullable(),
  activeNow: z.boolean(),
})

const miraklRebuildVerificationSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("verified"),
    offersRead: z.number().int().nonnegative(),
    checked: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("mismatched"),
    offersRead: z.number().int().nonnegative(),
    checked: z.number().int().nonnegative(),
    mismatches: z.array(
      z.object({
        sku: z.string(),
        expected: z.number(),
        actual: z.number().nullable(),
      })
    ),
  }),
  z.object({ kind: z.literal("unavailable"), reason: z.string() }),
])

export const miraklOfferRebuildReportSchema = z.object({
  kind: z.literal(MIRAKL_OFFER_REBUILD_KIND),
  marketplace: z.string().min(1),
  zeroAtOrBelow: liveOfferRebuildFloorSchema,
  rows: z.array(miraklRebuildRowSchema),
  rowCount: z.number().int().nonnegative(),
  totals: rebuildTotalsSchema,
  blocked: z.array(z.object({ sku: z.string(), reason: z.string(), field: z.string() })),
  header: z.array(z.string()),
  csv: z.object({ filename: z.string(), text: z.string() }).nullable(),
  committed: z.boolean(),
  reviewedJobId: z.string().nullable(),
  upload: z
    .object({
      importId: z.number().int().positive(),
      status: z.string().nullable(),
      linesInSuccess: z.number().int().nonnegative().nullable(),
      linesInError: z.number().int().nonnegative().nullable(),
      hasErrorReport: z.boolean().nullable(),
      terminal: z.boolean(),
    })
    .nullable(),
  verification: miraklRebuildVerificationSchema.nullable(),
})

export const octopiaRebuildRetentionSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("retained"),
    bytes: z.number().int().nonnegative(),
    maxBytes: z.number().int().positive(),
  }),
  z.object({
    state: z.literal("omitted"),
    bytes: z.number().int().nonnegative(),
    maxBytes: z.number().int().positive(),
    omitted: z.object({
      rows: z.number().int().nonnegative(),
      blocked: z.number().int().nonnegative(),
      artifact: z.boolean(),
      packageOffers: z.number().int().nonnegative(),
      verificationMismatches: z.number().int().nonnegative(),
    }),
  }),
])

const octopiaRebuildRowSchema = z.object({
  sellerExternalReference: z.string(),
  offerId: z.string(),
  condition: z.string(),
  productGtin: z.string(),
  productReference: z.string(),
  vendorUpdatedAt: z.string(),
  quantityBefore: z.number().int().nonnegative(),
  quantityAfter: z.number().int().nonnegative(),
  changes: z.boolean(),
})

const octopiaRebuildVerificationSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("verified"),
    offersRead: z.number().int().nonnegative(),
    checked: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("mismatched"),
    offersRead: z.number().int().nonnegative(),
    checked: z.number().int().nonnegative(),
    mismatches: z.array(
      z.object({
        sellerExternalReference: z.string(),
        expected: z.number().int().nonnegative(),
        actual: z.number().int().nonnegative().nullable(),
      })
    ),
  }),
  z.object({ kind: z.literal("unavailable"), reason: z.string() }),
])

export const octopiaLiveOfferRebuildReportSchema = z.object({
  kind: z.literal(OCTOPIA_LIVE_OFFER_REBUILD_KIND),
  marketplace: z.string().min(1),
  zeroAtOrBelow: liveOfferRebuildFloorSchema,
  rows: z.array(octopiaRebuildRowSchema),
  rowCount: z.number().int().nonnegative(),
  totals: rebuildTotalsSchema,
  blocked: z.array(
    z.object({
      sellerExternalReference: z.string().nullable(),
      offerId: z.string().nullable(),
      field: z.string(),
      reason: z.enum([
        "missing_seller_reference",
        "duplicate_seller_reference",
        "missing_offer_id",
        "missing_condition",
        "missing_product_gtin",
        "missing_product_reference",
        "missing_vendor_timestamp",
        "missing_seller_quantity",
        "ambiguous_seller_quantity",
        "unsupported_fulfillment_only",
      ]),
    })
  ),
  artifact: z
    .object({ filename: z.string(), format: z.literal("json"), text: z.string() })
    .nullable(),
  retention: octopiaRebuildRetentionSchema,
  committed: z.boolean(),
  submitted: z.boolean(),
  reviewedJobId: z.string().nullable(),
  package: z
    .object({
      packageId: z.string(),
      status: z.string().nullable(),
      integrated: z.number().int().nonnegative(),
      rejected: z.number().int().nonnegative(),
      terminal: z.boolean(),
      offers: z.array(
        z.object({
          sellerExternalReference: z.string().nullable(),
          status: z.string(),
          error: z.string().nullable(),
        })
      ),
    })
    .nullable(),
  verification: octopiaRebuildVerificationSchema.nullable(),
})

export const liveOfferRebuildReportSchema = z.discriminatedUnion("kind", [
  miraklOfferRebuildReportSchema,
  octopiaLiveOfferRebuildReportSchema,
])

export const liveOfferRebuildJobResultSchema = z.union([
  liveOfferRebuildReportSchema,
  liveOfferRebuildSubmissionReceiptSchema,
])

export type LiveOfferRebuildReport = z.infer<typeof liveOfferRebuildReportSchema>
export type LiveOfferRebuildSubmissionReceipt = z.infer<
  typeof liveOfferRebuildSubmissionReceiptSchema
>
export type MiraklOfferRebuildReport = z.infer<typeof miraklOfferRebuildReportSchema>
export type MiraklOfferRebuildRow = MiraklOfferRebuildReport["rows"][number]
export type MiraklOfferRebuildBlocked = MiraklOfferRebuildReport["blocked"][number]
export type MiraklOfferRebuildCsv = NonNullable<MiraklOfferRebuildReport["csv"]>
export type MiraklOfferRebuildTotals = MiraklOfferRebuildReport["totals"]
export type MiraklOfferRebuildUpload = NonNullable<MiraklOfferRebuildReport["upload"]>
export type MiraklOfferRebuildVerification = NonNullable<MiraklOfferRebuildReport["verification"]>
export type OctopiaLiveOfferRebuildReport = z.infer<typeof octopiaLiveOfferRebuildReportSchema>
export type OctopiaLiveOfferRebuildRow = OctopiaLiveOfferRebuildReport["rows"][number]
export type OctopiaLiveOfferRebuildBlocked = OctopiaLiveOfferRebuildReport["blocked"][number]
export type OctopiaLiveOfferRebuildTotals = OctopiaLiveOfferRebuildReport["totals"]
export type OctopiaLiveOfferRebuildArtifact = NonNullable<OctopiaLiveOfferRebuildReport["artifact"]>
export type OctopiaLiveOfferRebuildVerification = NonNullable<
  OctopiaLiveOfferRebuildReport["verification"]
>
